import React from 'react';
import { PlaybookAdherence } from '../../adherence/types';
import SummarySectionCard from './SummarySectionCard';
import { ProgressBar, scoreTone } from '../../ui/ProgressBar';

interface IntroSummaryCardProps {
  playbook?: PlaybookAdherence;
  openerPhaseTimestamp?: string;
  onViewDetails: () => void;
  onViewInTimeline?: () => void;
}

const IntroSummaryCard: React.FC<IntroSummaryCardProps> = ({
  playbook,
  openerPhaseTimestamp,
  onViewDetails,
  onViewInTimeline,
}) => {
  const hasGeneric = !!playbook?.intro_generic;
  const hasPlaybook = playbook?.was_used && playbook?.intro_coverage;
  const coveragePct = playbook?.intro_coverage?.coverage_pct;
  const genericScore = playbook?.intro_generic?.score;

  const displayScore = hasPlaybook && coveragePct != null ? coveragePct : genericScore;

  const badgeColor =
    displayScore == null
      ? 'bg-surface-subtle text-ink-muted border border-hairline'
      : displayScore >= 70
      ? 'bg-success-tint text-success border border-success'
      : displayScore >= 40
      ? 'bg-warning-tint text-warning border border-warning'
      : 'bg-error-tint text-error border border-error';

  const statusBadge = displayScore != null ? (
    <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${badgeColor}`}>{displayScore}%</span>
  ) : undefined;

  const coachingText = playbook?.intro_generic?.coaching;

  const missingMsg =
    !playbook
      ? 'Intro analysis requires re-analysis of this session.'
      : !hasGeneric
      ? 'Re-analyze this session to see generic intro analysis.'
      : undefined;

  const resultContent = (
    <div className="space-y-3">
      {hasPlaybook && playbook.intro_coverage && (
        <div className="bg-info-tint border border-info rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold text-info uppercase tracking-wider">Playbook Coverage</p>
            <span className="text-xs font-bold text-ink">
              {playbook.intro_coverage.total_delivered}/{playbook.intro_coverage.total_available} points
            </span>
          </div>
          <ProgressBar value={playbook.intro_coverage.coverage_pct} tone={scoreTone(playbook.intro_coverage.coverage_pct)} />
          {playbook.intro_coverage.intro_quality_summary && (
            <p className="text-xs text-ink mt-2">{playbook.intro_coverage.intro_quality_summary}</p>
          )}
        </div>
      )}
      {playbook?.intro_generic && (
        <div className="bg-surface-subtle border border-hairline rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold text-ink-muted uppercase tracking-wider">Generic Analysis</p>
            <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${badgeColor}`}>{genericScore}%</span>
          </div>
          <p className="text-xs text-ink-secondary">{playbook.intro_generic.summary}</p>
          {(playbook.intro_generic.what_worked || playbook.intro_generic.what_missed) && (
            <div className="mt-2 space-y-1 pt-2 border-t border-hairline">
              {playbook.intro_generic.what_worked && (
                <p className="text-[11px] text-success">
                  <span className="font-semibold">Worked: </span>{playbook.intro_generic.what_worked}
                </p>
              )}
              {playbook.intro_generic.what_missed && (
                <p className="text-[11px] text-error">
                  <span className="font-semibold">Missed: </span>{playbook.intro_generic.what_missed}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <SummarySectionCard
      title="Intro"
      statusBadge={statusBadge}
      resultContent={resultContent}
      coachingItems={coachingText ? [coachingText] : undefined}
      sectionKey="intro"
      onViewDetails={onViewDetails}
      onViewInTimeline={openerPhaseTimestamp ? onViewInTimeline : undefined}
      missingDataMessage={missingMsg}
    />
  );
};

export default IntroSummaryCard;
